import {
  ProjectsService,
  MeetingStartRequest,
  Meeting,
  MeetingUpdateRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { sortMeetingsByStartedAt } from '@/utils/helpers/meetingHelpers';

type MeetingStoreState = {
  meetings: Record<string, Meeting[]>;
  activeMeeting: Meeting | null;
};

export const useMeetingStore = defineStore('meetingStore', {
  state: (): MeetingStoreState => ({
    meetings: {},
    activeMeeting: null,
  }),

  getters: {
    getMeetings: (state) => (projectId: string) => {
      return sortMeetingsByStartedAt(state.meetings[projectId] || []);
    },
    meeting: (state) => (projectId: string, meetingId: string) => {
      const meeting = state.meetings[projectId]?.find(
        (m) => m.id === meetingId,
      );

      return meeting || null;
    },
  },
  actions: {
    async fetchActiveMeeting(projectId: string) {
      const { meetings } = await ProjectsService.getMeetings(
        projectId,
        'active',
      );
      this.activeMeeting = meetings[0] || null;
    },

    async loadAllMeetings(projectId: string) {
      const { meetings } = await ProjectsService.getMeetings(projectId);
      this.meetings[projectId] = meetings;
    },

    async startMeeting(projectId: string, payload: MeetingStartRequest) {
      const createdMeeting = await ProjectsService.startNewMeeting(
        projectId,
        payload,
      );

      if (!this.meetings[projectId]) {
        this.meetings[projectId] = [];
      }

      // Prepend: keeps the array in created_at DESC order (the API's order),
      // which the stable sort in getMeetings relies on as started_at tiebreaker.
      this.meetings[projectId].unshift(createdMeeting);
      this.activeMeeting = createdMeeting;
    },

    async updateMeeting(
      projectId: string,
      meetingId: string,
      payload: MeetingUpdateRequest,
    ) {
      const updatedMeeting = await ProjectsService.updateMeeting(
        projectId,
        meetingId,
        payload,
      );

      const persistedMeeting = this.meetings[projectId].find(
        (m) => m.id === meetingId,
      );
      if (persistedMeeting) {
        Object.assign(persistedMeeting, updatedMeeting);
      }

      if (updatedMeeting.id === this.activeMeeting?.id) {
        this.activeMeeting = updatedMeeting;
      }
    },

    async endMeeting(projectId: string, meetingId: string) {
      await ProjectsService.stopMeeting(projectId, meetingId);
      await this.loadAllMeetings(projectId);
      this.activeMeeting = null;
    },

    async deleteMeeting(projectId: string, meetingId: string) {
      await ProjectsService.deleteMeeting(projectId, meetingId);
      this.meetings[projectId] = this.meetings[projectId].filter(
        (m) => m.id !== meetingId,
      );
    },

    clearMeetingSummaryId(projectId: string, meetingId: string) {
      const persistedMeeting = this.meetings[projectId].find(
        (m) => m.id === meetingId,
      );

      if (persistedMeeting) {
        persistedMeeting.meeting_summary_id = null;
      }

      if (this.activeMeeting?.id === meetingId) {
        this.activeMeeting.meeting_summary_id = null;
      }
    },
  },
});
